import { useQuery } from "@tanstack/react-query";
import type { Role } from "gsintegrations";
import { roleRepository } from "../repositories/roleRepository";

/**
 * Query key factory for roles
 */
export const roleQueryKeys = {
  all: (terreiroId: string) => ["terreiro", terreiroId, "roles"] as const,
  detail: (roleId: string) => ["role", roleId] as const,
  permissions: (roleId: string) => ["role", roleId, "permissions"] as const,
};

/**
 * Hook to get all roles for a terreiro
 */
interface UseRolesQueryOptions {
  terreiroId: string;
  enabled?: boolean;
}

export const useRolesQuery = ({
  terreiroId,
  enabled = true,
}: UseRolesQueryOptions) => {
  return useQuery<Role[]>({
    queryKey: roleQueryKeys.all(terreiroId),
    queryFn: () => roleRepository.getAll(terreiroId),
    enabled: enabled && !!terreiroId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
    refetchOnMount: "always",
  });
};

/**
 * Hook to get role by ID
 */
interface UseRoleByIdQueryOptions {
  roleId: string;
  terreiroId?: string;
  enabled?: boolean;
}

export const useRoleByIdQuery = ({
  roleId,
  terreiroId,
  enabled = true,
}: UseRoleByIdQueryOptions) => {
  return useQuery<Role>({
    queryKey: roleQueryKeys.detail(roleId),
    queryFn: () => roleRepository.getById(roleId, terreiroId),
    enabled: enabled && !!roleId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    refetchOnMount: "always",
  });
};

/**
 * Hook to get permissions for a specific role
 */
interface UseRolePermissionsQueryOptions {
  roleId: string;
  terreiroId?: string;
  enabled?: boolean;
}

export const useRolePermissionsQuery = ({
  roleId,
  terreiroId,
  enabled = true,
}: UseRolePermissionsQueryOptions) => {
  return useQuery<string[]>({
    queryKey: roleQueryKeys.permissions(roleId),
    queryFn: () => roleRepository.getPermissions(roleId, terreiroId),
    enabled: enabled && !!roleId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    refetchOnMount: "always",
  });
};
